// Minimal Quake-style vector math. Vectors are plain number arrays so the
// shared simulation code has no rendering dependencies (runs in a browser or
// on a Node server for future multiplayer).

export type Vec3 = [number, number, number];

export const vec3 = (x = 0, y = 0, z = 0): Vec3 => [x, y, z];
export const copy = (a: Vec3): Vec3 => [a[0], a[1], a[2]];

export function set(out: Vec3, x: number, y: number, z: number): Vec3 {
  out[0] = x;
  out[1] = y;
  out[2] = z;
  return out;
}

export function copyTo(out: Vec3, a: Vec3): Vec3 {
  out[0] = a[0];
  out[1] = a[1];
  out[2] = a[2];
  return out;
}

export const dot = (a: Vec3, b: Vec3): number => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
export const length = (a: Vec3): number => Math.sqrt(a[0] * a[0] + a[1] * a[1] + a[2] * a[2]);

export function add(out: Vec3, a: Vec3, b: Vec3): Vec3 {
  out[0] = a[0] + b[0];
  out[1] = a[1] + b[1];
  out[2] = a[2] + b[2];
  return out;
}

export function sub(out: Vec3, a: Vec3, b: Vec3): Vec3 {
  out[0] = a[0] - b[0];
  out[1] = a[1] - b[1];
  out[2] = a[2] - b[2];
  return out;
}

export function scale(out: Vec3, a: Vec3, s: number): Vec3 {
  out[0] = a[0] * s;
  out[1] = a[1] * s;
  out[2] = a[2] * s;
  return out;
}

/** out = a + b * s (VectorMA) */
export function ma(out: Vec3, a: Vec3, s: number, b: Vec3): Vec3 {
  out[0] = a[0] + b[0] * s;
  out[1] = a[1] + b[1] * s;
  out[2] = a[2] + b[2] * s;
  return out;
}

export function cross(out: Vec3, a: Vec3, b: Vec3): Vec3 {
  const x = a[1] * b[2] - a[2] * b[1];
  const y = a[2] * b[0] - a[0] * b[2];
  const z = a[0] * b[1] - a[1] * b[0];
  out[0] = x;
  out[1] = y;
  out[2] = z;
  return out;
}

/** Normalizes in place, returns the original length (VectorNormalize). */
export function normalize(v: Vec3): number {
  const len = length(v);
  if (len) {
    const inv = 1 / len;
    v[0] *= inv;
    v[1] *= inv;
    v[2] *= inv;
  }
  return len;
}

/** VectorNormalize2: writes normalized copy to out, returns length. */
export function normalize2(v: Vec3, out: Vec3): number {
  const len = length(v);
  if (len) {
    const inv = 1 / len;
    out[0] = v[0] * inv;
    out[1] = v[1] * inv;
    out[2] = v[2] * inv;
  } else {
    out[0] = out[1] = out[2] = 0;
  }
  return len;
}

export const DEG2RAD = Math.PI / 180;

/** Quake AngleVectors (angles in degrees: pitch, yaw, roll). */
export function angleVectors(angles: Vec3, forward: Vec3 | null, right: Vec3 | null, up: Vec3 | null): void {
  let angle = angles[1] * DEG2RAD;
  const sy = Math.sin(angle);
  const cy = Math.cos(angle);
  angle = angles[0] * DEG2RAD;
  const sp = Math.sin(angle);
  const cp = Math.cos(angle);
  angle = angles[2] * DEG2RAD;
  const sr = Math.sin(angle);
  const cr = Math.cos(angle);

  if (forward) set(forward, cp * cy, cp * sy, -sp);
  if (right) set(right, -1 * sr * sp * cy + -1 * cr * -sy, -1 * sr * sp * sy + -1 * cr * cy, -1 * sr * cp);
  if (up) set(up, cr * sp * cy + -sr * -sy, cr * sp * sy + -sr * cy, cr * cp);
}

/** Quake vectoangles: direction -> [pitch, yaw, 0] in degrees. */
export function vectoangles(v: Vec3, out: Vec3): Vec3 {
  let yaw: number;
  let pitch: number;
  if (v[1] === 0 && v[0] === 0) {
    yaw = 0;
    pitch = v[2] > 0 ? 90 : 270;
  } else {
    if (v[0]) yaw = (Math.atan2(v[1], v[0]) * 180) / Math.PI;
    else if (v[1] > 0) yaw = 90;
    else yaw = 270;
    if (yaw < 0) yaw += 360;
    const forward = Math.sqrt(v[0] * v[0] + v[1] * v[1]);
    pitch = (Math.atan2(v[2], forward) * 180) / Math.PI;
    if (pitch < 0) pitch += 360;
  }
  return set(out, -pitch, yaw, 0);
}

export const ANGLE2SHORT = (x: number): number => Math.trunc((x * 65536) / 360) & 65535;
export const SHORT2ANGLE = (x: number): number => x * (360 / 65536);

/** Round half to even, like the x87 FPU default rounding mode used by SnapVector. */
export function roundEven(x: number): number {
  const r = Math.round(x);
  return Math.abs(x % 1) === 0.5 && r % 2 !== 0 ? r - 1 : r;
}

/** Store a vector with float32 precision, as the original engine does. */
export function toFloat32(v: Vec3): Vec3 {
  v[0] = Math.fround(v[0]);
  v[1] = Math.fround(v[1]);
  v[2] = Math.fround(v[2]);
  return v;
}
